import { 
    EditCalendar, 
    LocationOn, 
    People, 
    Person, 
    TrendingUp, 
    TrendingDown,
    MedicalServices,
    CalendarMonth,
    Build,
    Event,
    WbSunny,
    Thermostat,
    Water,
    MeetingRoom,
    Notifications,
    Battery20,
    Battery50,
    BatteryFull
} from "@mui/icons-material";
import { 
    Avatar, 
    Box, 
    Button, 
    Card, 
    CardContent, 
    Grid, 
    Typography,
    Skeleton,
    LinearProgress,
    Chip,
    IconButton,
    Divider,
    useTheme,
    alpha
} from "@mui/material";
import { useEffect, useState, useCallback, useMemo, memo, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import socket from "../utils/socketConfig";
import { LazyAlertsWidget } from "../components/LazyComponents";
import MaintenanceWidget from "../components/MaintenanceWidget";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "../hooks/usePerformanceOptimizations";
import CountUp from 'react-countup';
import { motion } from 'framer-motion';

// Registra gli elementi necessari
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

// Colori per i bordi delle card statistiche
const cardBorderColors = [
    '#667eea', // Viola
    '#f5576c', // Rosa
    '#4facfe', // Azzurro
    '#43e97b', // Verde
    '#fa709a', // Arancio
    '#a18cd1', // Lavanda
];

// Icone per ogni tipo di card
const cardIcons = [
    <Person fontSize="large" />,
    <People fontSize="large" />,
    <MedicalServices fontSize="large" />,
    <CalendarMonth fontSize="large" />,
    <Event fontSize="large" />,
    <Build fontSize="large" />,
];

// Animazione container
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

// Animazione singolo item
const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            type: "spring",
            stiffness: 100
        }
    }
};

// Componente card memoizzato con bordi colorati
const DashboardCard = memo(({ item, index, t }) => {
    const theme = useTheme();
    const isLoading = item.value === '...';
    const borderColor = cardBorderColors[index % cardBorderColors.length];
    
    return (
        <Grid item xs={6} sm={6} md={4} lg={2}>
            <motion.div variants={itemVariants}>
                <Card 
                    sx={{ 
                        p: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        background: theme.palette.background.paper,
                        borderLeft: `4px solid ${borderColor}`,
                        minHeight: { xs: 120, sm: 140 },
                        height: '100%',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 24px ${alpha(borderColor, 0.25)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <CardContent sx={{ position: 'relative', zIndex: 1, p: { xs: 1.5, sm: 2 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography 
                                    variant="h3" 
                                    sx={{ 
                                        fontWeight: 700,
                                        mt: 0.5,
                                        color: 'text.primary',
                                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                                    }}
                                >
                                    {isLoading ? (
                                        <Skeleton 
                                            width={60} 
                                            height={50} 
                                        />
                                    ) : (
                                        <CountUp 
                                            end={parseInt(item.value) || 0} 
                                            duration={2} 
                                            separator="."
                                        />
                                    )}
                                </Typography>
                            </Box>
                            <Avatar
                                sx={{
                                    bgcolor: alpha(borderColor, 0.15),
                                    color: borderColor,
                                    width: { xs: 36, sm: 48 },
                                    height: { xs: 36, sm: 48 },
                                    flexShrink: 0,
                                    '& svg': {
                                        fontSize: { xs: 20, sm: 24 }
                                    }
                                }}
                            >
                                {cardIcons[index % cardIcons.length]}
                            </Avatar>
                        </Box>
                        
                        {/* Trend indicator */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mt: 1.5, gap: 0.5 }}>
                            <TrendingUp sx={{ fontSize: 16, color: borderColor }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {t('dashboard.trendThisMonth', '+12% questo mese')}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </motion.div>
        </Grid>
    );
});

DashboardCard.displayName = 'DashboardCard';

// Componente sensore memoizzato con design migliorato
const SensorCard = memo(({ sensor, navigate, t }) => {
    const theme = useTheme();
    const handleNavigate = useCallback(() => {
        navigate(`/sensors/${sensor.sensor_id}`);
    }, [navigate, sensor.sensor_id]);

    // Calcola colore temperatura
    const getTempColor = (temp) => {
        if (temp < 18) return '#4facfe';
        if (temp > 26) return '#f5576c';
        return '#43e97b';
    };

    return (
        <Grid item xs={12} sm={6} md={4}>
            <motion.div variants={itemVariants}>
                <Card 
                    sx={{ 
                        p: 0,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        height: '100%',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                    onClick={handleNavigate}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    sx={{
                                        fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t('dashboard.widgets.sensors.title', "Sensore")} {sensor.sensor_id}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                >
                                    {sensor.room_number ? `Camera ${sensor.room_number}` : (sensor.corridor_floor_name || sensor.corridor_floor_id) ? `Corridoio ${sensor.corridor_floor_name || sensor.corridor_floor_id}${sensor.corridor_wing_name ? ' - ' + sensor.corridor_wing_name : ''}` : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Chip 
                                    label={t('dashboard.sensor.online', 'Online')}
                                    size="small" 
                                    color="success"
                                    sx={{ fontWeight: 600, flexShrink: 0 }}
                                />
                                {sensor.battery_level !== null && sensor.battery_level !== undefined && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getBatteryIcon(sensor.battery_level)}
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                            {sensor.battery_level}%
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box 
                                    sx={{ 
                                        p: { xs: 1.5, sm: 2 }, 
                                        borderRadius: 2,
                                        background: alpha(getTempColor(sensor.temperature), 0.1),
                                        textAlign: 'center'
                                    }}
                                >
                                    <Thermostat sx={{ color: getTempColor(sensor.temperature), fontSize: { xs: 24, sm: 32 } }} />
                                    <Typography 
                                        variant="h4" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            color: getTempColor(sensor.temperature),
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}
                                    >
                                        {sensor.temperature}°
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                    >
                                        {t('dashboard.sensor.temperature', 'Temperatura')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box 
                                    sx={{ 
                                        p: { xs: 1.5, sm: 2 }, 
                                        borderRadius: 2,
                                        background: alpha(theme.palette.info.main, 0.1),
                                        textAlign: 'center'
                                    }}
                                >
                                    <Water sx={{ color: theme.palette.info.main, fontSize: { xs: 24, sm: 32 } }} />
                                    <Typography 
                                        variant="h4" 
                                        fontWeight="bold" 
                                        color="info.main"
                                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                                    >
                                        {sensor.humidity}%
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                    >
                                        {t('dashboard.sensor.humidity', 'Umidità')}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleNavigate}
                            sx={{ 
                                mt: 2,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                py: { xs: 0.75, sm: 1 }
                            }}
                        >
                            {t('dashboard.widgets.sensors.viewChart', "Visualizza Grafico")}
                        </Button>
                    </CardContent>
                </Card>
            </motion.div>
        </Grid>
    );
});

SensorCard.displayName = 'SensorCard';

// Componente chart ottimizzato con intersection observer
const OptimizedChart = memo(({ data, options, type = 'Bar', title }) => {
  const [chartRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '50px'
  });

  const ChartComponent = type === 'Bar' ? Bar : Line;

  return (
    <Grid item xs={12} sm={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h4">{title}</Typography>
        <div ref={chartRef} style={{ minHeight: '300px' }}>
          {isVisible ? (
            <ChartComponent data={data} options={options} />
          ) : (
            <div style={{ 
              height: '300px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRadius: '4px'
            }}>
              <Typography color="textSecondary">
                Chart will load when visible
              </Typography>
            </div>
          )}
        </div>
      </Card>
    </Grid>
  );
});

OptimizedChart.displayName = 'OptimizedChart';

// Widget sensore T&H
const SensorThWidget = memo(({ t, sensor, navigate }) => {
  const theme = useTheme();
  
  const getTempColor = (temp) => {
    if (temp < 18) return '#4facfe';
    if (temp > 26) return '#f5576c';
    return '#43e97b';
  };

  const handleNavigate = useCallback(() => {
    if (sensor && navigate) {
      navigate(`/app-conviventi/sensors/${sensor.sensor_id}`);
    }
  }, [navigate, sensor]);
  
    const borderColor = sensor ? getTempColor(sensor.temperature) : '#667eea';

    return (
        <motion.div variants={itemVariants}>
            <Card 
                sx={{ 
                    p: 0,
                    overflow: 'hidden',
                    background: theme.palette.background.paper,
                    borderTop: `4px solid ${borderColor}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                    },
                    transition: 'all 0.3s ease',
                }}
            >
        <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                {t('dashboard.widgets.sensors.title', "Sensore Ambiente")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {sensor && (sensor.room_number ? `Camera ${sensor.room_number}` : (sensor.corridor_floor_name || sensor.corridor_floor_id) ? `Corridoio ${sensor.corridor_floor_name || sensor.corridor_floor_id}${sensor.corridor_wing_name ? ' - ' + sensor.corridor_wing_name : ''}` : '')}
                            </Typography>
                        </Box>
                        {sensor && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Chip 
                                    label={t('dashboard.sensor.online', 'Online')}
                                    size="small" 
                                    color="success"
                                    sx={{ fontWeight: 600 }}
                                />
                                {sensor.battery_level !== null && sensor.battery_level !== undefined && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getBatteryIcon(sensor.battery_level)}
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {sensor.battery_level}%
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
          
          {sensor ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      background: alpha(getTempColor(sensor.temperature), 0.1),
                      textAlign: 'center'
                    }}
                  >
                    <Thermostat sx={{ color: getTempColor(sensor.temperature), fontSize: 32 }} />
                    <Typography variant="h4" fontWeight="bold" sx={{ color: getTempColor(sensor.temperature) }}>
                      {sensor.temperature}°
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.sensor.temperature', 'Temperatura')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      background: alpha(theme.palette.info.main, 0.1),
                      textAlign: 'center'
                    }}
                  >
                    <Water sx={{ color: theme.palette.info.main, fontSize: 32 }} />
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {sensor.humidity}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.sensor.humidity', 'Umidità')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Button
                fullWidth
                variant="contained"
                onClick={handleNavigate}
                sx={{ mt: 2 }}
              >
                {t('dashboard.widgets.sensors.viewChart', "Visualizza Grafico")}
              </Button>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="rectangular" width="50%" height={120} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="50%" height={120} sx={{ borderRadius: 2 }} />
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
});

SensorThWidget.displayName = 'SensorThWidget';

// Widget meteo ottimizzato con design professionale
const WeatherWidget = memo(({ t, temp, weather, humidity, weatherIcon }) => {
  const theme = useTheme();
  const borderColor = '#667eea';
  
  // Mappa icone meteo
  const getWeatherIcon = (description) => {
    if (!description) return '☀️';
    const desc = description.toLowerCase();
    if (desc.includes('clear') || desc.includes('sereno')) return '☀️';
    if (desc.includes('cloud') || desc.includes('nuvoloso') || desc.includes('nubi')) return '☁️';
    if (desc.includes('rain') || desc.includes('pioggia')) return '🌧️';
    if (desc.includes('storm') || desc.includes('temporale')) return '⛈️';
    if (desc.includes('snow') || desc.includes('neve')) return '❄️';
    if (desc.includes('mist') || desc.includes('fog') || desc.includes('nebbia')) return '🌫️';
    if (desc.includes('wind') || desc.includes('vento')) return '💨';
    return '🌤️';
  };
  
  return (
    <motion.div variants={itemVariants}>
      <Card 
        sx={{ 
          p: 0,
          overflow: 'hidden',
          background: theme.palette.background.paper,
          borderTop: `4px solid ${borderColor}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 24px ${alpha(borderColor, 0.25)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: alpha(borderColor, 0.15), width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } }}>
                  <WbSunny sx={{ color: borderColor, fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  color="text.primary"
                  sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' } }}
                >
                  {t('dashboard.widgets.weather.title', "Meteo Firenze")}
                </Typography>
              </Box>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  ml: 5,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textTransform: 'capitalize'
                }}
              >
                {getWeatherIcon(weather)} {weather}
              </Typography>
            </Box>
          </Box>
          
          {temp && weather ? (
            <Box>
              {/* Temperatura e umidità in grid come il sensore */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box 
                    sx={{ 
                      p: { xs: 1.5, sm: 2 }, 
                      borderRadius: 2,
                      background: alpha('#f5576c', 0.1),
                      textAlign: 'center'
                    }}
                  >
                    <Thermostat sx={{ color: '#f5576c', fontSize: { xs: 24, sm: 32 } }} />
                    <Typography 
                      variant="h4" 
                      fontWeight="bold" 
                      sx={{ 
                        color: '#f5576c',
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                      }}
                    >
                      {temp}°
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    >
                      {t('dashboard.sensor.temperature', 'Temperatura')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box 
                    sx={{ 
                      p: { xs: 1.5, sm: 2 }, 
                      borderRadius: 2,
                      background: alpha('#4facfe', 0.1),
                      textAlign: 'center'
                    }}
                  >
                    <Water sx={{ color: '#4facfe', fontSize: { xs: 24, sm: 32 } }} />
                    <Typography 
                      variant="h4" 
                      fontWeight="bold" 
                      color="info.main"
                      sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                    >
                      {humidity}%
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    >
                      {t('dashboard.sensor.humidityLabel', 'umidità')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Button
                fullWidth
                variant="contained"
                onClick={() => window.open('https://www.ilmeteo.it/meteo/Firenze', '_blank')}
                sx={{ 
                  mt: 2,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 0.75, sm: 1 }
                }}
              >
                {t('dashboard.widgets.weather.viewDetails', 'Dettagli Meteo')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Skeleton variant="circular" width={60} height={60} />
              <Box sx={{ width: '100%' }}>
                <Skeleton width="80%" height={40} />
                <Skeleton width="60%" height={20} />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
});

WeatherWidget.displayName = 'WeatherWidget';

// Helper per icona batteria
const getBatteryIcon = (batteryLevel) => {
  if (!batteryLevel || batteryLevel === null) return null;
  
  if (batteryLevel <= 20) {
    return <Battery20 sx={{ fontSize: 16, color: '#f5576c' }} />;
  } else if (batteryLevel <= 50) {
    return <Battery50 sx={{ fontSize: 16, color: '#fa709a' }} />;
  } else {
    return <BatteryFull sx={{ fontSize: 16, color: '#43e97b' }} />;
  }
};

// Widget appuntamenti con design migliorato
const AppointmentsWidget = memo(({ appointments, navigate, t }) => {
    const theme = useTheme();
    
    const getTypeLabel = (type) => {
        const types = {
            'visita_medica': t('appointments.types.medical', 'Visita Medica'),
            'visita_parenti': t('appointments.types.family', 'Visita Parenti'),
            'ricovero': t('appointments.types.hospitalization', 'Ricovero'),
            'vacanza': t('appointments.types.vacation', 'Vacanza'),
            'altro': t('appointments.types.other', 'Altro')
        };
        return types[type] || type;
    };

    const getTypeColor = (type) => {
        const colors = {
            'visita_medica': '#4facfe',
            'visita_parenti': '#43e97b',
            'ricovero': '#f5576c',
            'vacanza': '#fa709a',
            'altro': '#667eea'
        };
        return colors[type] || '#667eea';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5); // HH:MM
    };

    return (
        <Grid item xs={12} md={4}>
            <motion.div variants={itemVariants} style={{ height: '100%' }}>
                <Card sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%', 
                    minHeight: 400, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    borderTop: `4px solid ${theme.palette.primary.main}` 
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Avatar sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.15), 
                                width: { xs: 32, sm: 36 }, 
                                height: { xs: 32, sm: 36 },
                                flexShrink: 0
                            }}>
                                <CalendarMonth sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                            </Avatar>
                            <Typography 
                                variant="h6" 
                                fontWeight="bold"
                                sx={{ 
                                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t('dashboard.widgets.appointments.title', 'Prossimi Appuntamenti')}
                            </Typography>
                        </Box>
                        <Chip 
                            label={appointments.length} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 700, flexShrink: 0 }}
                        />
                    </Box>
                    
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {appointments.length > 0 ? (
                            <Box>
                                {appointments.map((apt, index) => (
                                    <Box 
                                        key={apt.appointment_id} 
                                        sx={{ 
                                            mb: 2, 
                                            p: { xs: 1.5, sm: 2 }, 
                                            borderRadius: 2,
                                            backgroundColor: alpha(getTypeColor(apt.appointment_type), 0.08),
                                            borderLeft: `4px solid ${getTypeColor(apt.appointment_type)}`,
                                            cursor: 'pointer',
                                            '&:hover': { 
                                                backgroundColor: alpha(getTypeColor(apt.appointment_type), 0.15),
                                                transform: 'translateX(4px)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => navigate('appointments')}
                                    >
                                        <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                            <Box sx={{ minWidth: { xs: 60, sm: 70 }, flexShrink: 0 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: getTypeColor(apt.appointment_type), 
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    }}
                                                >
                                                    {formatDate(apt.appointment_date)}
                                                </Typography>
                                                {apt.start_time && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    >
                                                        {formatTime(apt.start_time)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="medium" 
                                                    sx={{
                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {apt.title || getTypeLabel(apt.appointment_type)}
                                                </Typography>
                                                <Chip 
                                                    label={getTypeLabel(apt.appointment_type)} 
                                                    size="small"
                                                    sx={{ 
                                                        mt: 0.5,
                                                        height: { xs: 18, sm: 20 },
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        bgcolor: alpha(getTypeColor(apt.appointment_type), 0.2),
                                                        color: getTypeColor(apt.appointment_type)
                                                    }}
                                                />
                                                {apt.location && (
                                                    <Typography 
                                                        variant="caption" 
                                                        display="block" 
                                                        color="text.secondary" 
                                                        sx={{ 
                                                            mt: 0.5,
                                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        📍 {apt.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Avatar sx={{ 
                                    width: { xs: 56, sm: 64 }, 
                                    height: { xs: 56, sm: 64 }, 
                                    bgcolor: alpha(theme.palette.text.disabled, 0.1), 
                                    mx: 'auto', 
                                    mb: 2 
                                }}>
                                    <CalendarMonth sx={{ fontSize: { xs: 28, sm: 32 }, color: 'text.disabled' }} />
                                </Avatar>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    {t('dashboard.widgets.appointments.noAppointments', 'Nessun appuntamento nei prossimi 7 giorni')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    
                    <Button 
                        fullWidth
                        variant="contained" 
                        onClick={() => navigate('appointments')}
                        startIcon={<CalendarMonth />}
                        sx={{ 
                            mt: 2,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.75, sm: 1 }
                        }}
                    >
                        {appointments.length > 0 ? t('dashboard.widgets.appointments.viewAll', 'Vedi tutti') : t('dashboard.widgets.appointments.createNew', 'Crea Appuntamento')}
                    </Button>
                </Card>
            </motion.div>
        </Grid>
    );
});

AppointmentsWidget.displayName = 'AppointmentsWidget';

// Widget Eventi con design migliorato
const EventsWidget = memo(({ events, navigate, t }) => {
    const theme = useTheme();
    
    const getTypeLabel = (type) => {
        const types = {
            'compleanno': t('events.types.compleanno', 'Compleanno 🎂'),
            'ricorrenza': t('events.types.ricorrenza', 'Ricorrenza 🎉'),
            'festa': t('events.types.festa', 'Festa 🎊'),
            'attivita': t('events.types.attivita', 'Attività 🎨'),
            'gita': t('events.types.gita', 'Gita 🚌'),
            'conferenza': t('events.types.conferenza', 'Conferenza 🎤'),
            'spettacolo': t('events.types.spettacolo', 'Spettacolo 🎭'),
            'altro': t('events.types.altro', 'Altro')
        };
        return types[type] || type;
    };

    const getTypeColor = (type) => {
        const colors = {
            'compleanno': '#fa709a',
            'ricorrenza': '#667eea',
            'festa': '#f093fb',
            'attivita': '#43e97b',
            'gita': '#4facfe',
            'conferenza': '#a18cd1',
            'spettacolo': '#fee140',
            'altro': '#764ba2'
        };
        return colors[type] || '#667eea';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5); // HH:MM
    };

    return (
        <Grid item xs={12} md={4}>
            <motion.div variants={itemVariants} style={{ height: '100%' }}>
                <Card sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%', 
                    minHeight: 400, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    borderTop: `4px solid ${theme.palette.primary.main}` 
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Avatar sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.15), 
                                width: { xs: 32, sm: 36 }, 
                                height: { xs: 32, sm: 36 },
                                flexShrink: 0
                            }}>
                                <Event sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
                            </Avatar>
                            <Typography 
                                variant="h6" 
                                fontWeight="bold"
                                sx={{ 
                                    fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t('dashboard.widgets.events.title', 'Prossimi Eventi')}
                            </Typography>
                        </Box>
                        <Chip 
                            label={events.length} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 700, flexShrink: 0 }}
                        />
                    </Box>
                    
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {events.length > 0 ? (
                            <Box>
                                {events.map((evt, index) => (
                                    <Box 
                                        key={evt.event_id} 
                                        sx={{ 
                                            mb: 2, 
                                            p: { xs: 1.5, sm: 2 }, 
                                            borderRadius: 2,
                                            backgroundColor: alpha(getTypeColor(evt.event_type), 0.08),
                                            borderLeft: `4px solid ${getTypeColor(evt.event_type)}`,
                                            cursor: 'pointer',
                                            '&:hover': { 
                                                backgroundColor: alpha(getTypeColor(evt.event_type), 0.15),
                                                transform: 'translateX(4px)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => navigate('calendar')}
                                    >
                                        <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                            <Box sx={{ minWidth: { xs: 60, sm: 70 }, flexShrink: 0 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: getTypeColor(evt.event_type), 
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    }}
                                                >
                                                    {formatDate(evt.event_date)}
                                                </Typography>
                                                {evt.event_time && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    >
                                                        {formatTime(evt.event_time)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight="medium" 
                                                    sx={{
                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {evt.title}
                                                </Typography>
                                                <Chip 
                                                    label={getTypeLabel(evt.event_type)} 
                                                    size="small"
                                                    sx={{ 
                                                        mt: 0.5,
                                                        height: { xs: 18, sm: 20 },
                                                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                        bgcolor: alpha(getTypeColor(evt.event_type), 0.2),
                                                        color: getTypeColor(evt.event_type)
                                                    }}
                                                />
                                                {evt.location && (
                                                    <Typography 
                                                        variant="caption" 
                                                        display="block" 
                                                        color="text.secondary" 
                                                        sx={{ 
                                                            mt: 0.5,
                                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        📍 {evt.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Avatar sx={{ 
                                    width: { xs: 56, sm: 64 }, 
                                    height: { xs: 56, sm: 64 }, 
                                    bgcolor: alpha(theme.palette.text.disabled, 0.1), 
                                    mx: 'auto', 
                                    mb: 2 
                                }}>
                                    <Event sx={{ fontSize: { xs: 28, sm: 32 }, color: 'text.disabled' }} />
                                </Avatar>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    {t('dashboard.widgets.events.noEvents', 'Nessun evento nei prossimi 7 giorni')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    
                    <Button 
                        fullWidth
                        variant="contained" 
                        onClick={() => navigate('calendar')}
                        startIcon={<Event />}
                        sx={{ 
                            mt: 2,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.75, sm: 1 }
                        }}
                    >
                        {events.length > 0 ? t('dashboard.widgets.events.viewAll', 'Vedi tutti') : t('dashboard.widgets.events.createNew', 'Crea Evento')}
                    </Button>
                </Card>
            </motion.div>
        </Grid>
    );
});

EventsWidget.displayName = 'EventsWidget';

const DashboardPage = () => {
    const { t } = useTranslation("global");
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth(); // Aggiungi questo per controllare se il token è disponibile
    
    // Stati semplificati per evitare errori API
    const [elderlyCount, setElderlyCount] = useState(0);
    const [nursesCount, setNursesCount] = useState(0);
    const [doctorsCount, setDoctorsCount] = useState(0);
    const [appointmentsCount, setAppointmentsCount] = useState(0);
    const [eventsCount, setEventsCount] = useState(0);
    const [maintenanceCount, setMaintenanceCount] = useState(0);
    const [elderlyLoading, setElderlyLoading] = useState(false);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [sensors, setSensors] = useState([]);
    const [sensorsLoading, setSensorsLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [temp, setTemp] = useState(null);
    const [weather, setWeather] = useState("");
    const [humidity, setHumidity] = useState(null);
    const [weatherIcon, setWeatherIcon] = useState("");
    const [sensorTh, setSensorTh] = useState(null);
    
    // Caricamento alerts semplificato - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadAlerts = async () => {
            try {
                setAlertsLoading(true);
                const alertsResponse = await axiosPrivate.get('/alerts');
                setAlerts(alertsResponse.data || []);
            } catch (error) {
                console.error('Errore caricamento alerts:', error);
                setAlerts([]);
            } finally {
                setAlertsLoading(false);
            }
        };
        
        loadAlerts();
    }, [axiosPrivate, auth?.accessToken]);
        
    // Caricamento sensori semplificato con fallback - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadSensors = async () => {
            try {
                setSensorsLoading(true);
                // Use /sensors to include room/corridor associations
                const response = await axiosPrivate.get("/sensors");
                const uniqueSensors = response.data.reduce((acc, record) => {
                    if (!acc.find((sensor) => sensor.sensor_id === record.sensor_id)) {
                        acc.push({
                            sensor_id: record.sensor_id,
                            temperature: record.temperature,
                            humidity: record.humidity,
                            battery_level: record.battery_level,
                            room_id: record.room_id,
                            room_number: record.room_number,
                            floor_number: record.floor_number,
                            wing: record.wing,
                            corridor_floor_id: record.corridor_floor_id,
                            corridor_wing_id: record.corridor_wing_id,
                            corridor_floor_name: record.corridor_floor_name,
                            corridor_wing_name: record.corridor_wing_name
                        });
                    }
                    return acc;
                }, []);
                setSensors(uniqueSensors);
            } catch (error) {
                console.error('Errore caricamento sensori:', error);
                // Fallback con dati di esempio
                setSensors([
                    { sensor_id: 1, temperature: 22, humidity: 45, battery_level: 85 },
                    { sensor_id: 2, temperature: 24, humidity: 50, battery_level: 90 }
                ]);
            } finally {
                setSensorsLoading(false);
            }
        };
        
        loadSensors();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento appuntamenti imminenti - prossimi 7 giorni
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadAppointments = async () => {
            try {
                setAppointmentsLoading(true);
                const response = await axiosPrivate.get('/appointments');
                
                // Filtra e ordina solo gli appuntamenti futuri (prossimi 7 giorni)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeek = new Date(today);
                nextWeek.setDate(nextWeek.getDate() + 7);
                
                const upcomingAppointments = response.data
                    .filter(apt => {
                        const aptDate = new Date(apt.appointment_date);
                        return aptDate >= today && aptDate <= nextWeek && apt.status !== 'completed' && apt.status !== 'cancelled';
                    })
                    .sort((a, b) => {
                        const dateA = new Date(a.appointment_date + ' ' + a.start_time);
                        const dateB = new Date(b.appointment_date + ' ' + b.start_time);
                        return dateA - dateB;
                    })
                    .slice(0, 5); // Mostra solo i primi 5
                
                // Conta tutti gli appuntamenti attivi (non completati/cancellati)
                const totalActiveAppointments = response.data.filter(apt => 
                    apt.status !== 'completed' && apt.status !== 'cancelled'
                ).length;
                
                setAppointments(upcomingAppointments);
                setAppointmentsCount(totalActiveAppointments);
            } catch (error) {
                console.error('Errore caricamento appuntamenti:', error);
                setAppointments([]);
            } finally {
                setAppointmentsLoading(false);
            }
        };
        
        loadAppointments();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento eventi
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const loadEvents = async () => {
            try {
                const response = await axiosPrivate.get('/events');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeek = new Date(today);
                nextWeek.setDate(nextWeek.getDate() + 7);
                
                // Filtra eventi futuri prossimi 7 giorni
                const upcomingEvents = response.data
                    .filter(evt => {
                        const evtDate = new Date(evt.event_date);
                        return evtDate >= today && evtDate <= nextWeek;
                    })
                    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
                    .slice(0, 3); // Massimo 3
                
                setEvents(upcomingEvents);
                
                // Conta tutti gli eventi futuri
                const activeEvents = response.data.filter(evt => {
                    const evtDate = new Date(evt.event_date);
                    return evtDate >= today;
                }).length;
                setEventsCount(activeEvents);
            } catch (error) {
                console.error('Errore caricamento eventi:', error);
                setEventsCount(0);
                setEvents([]);
            }
        };
        
        loadEvents();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento manutenzioni
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const loadMaintenance = async () => {
            try {
                const response = await axiosPrivate.get('/maintenance/requests');
                const activeMaintenance = response.data.filter(m => 
                    m.status !== 'completata' && m.status !== 'annullata'
                ).length;
                setMaintenanceCount(activeMaintenance);
            } catch (error) {
                console.error('Errore caricamento manutenzioni:', error);
                setMaintenanceCount(0);
            }
        };
        
        loadMaintenance();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento dati semplificato con fallback - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadData = async () => {
            try {
                setElderlyLoading(true);
                const [elderlyResponse, nursesResponse, doctorsResponse] = await Promise.all([
                    axiosPrivate.get('/elderly/count'),
                    axiosPrivate.get('/nurses'),
                    axiosPrivate.get('/doctors')
                ]);
                
                setElderlyCount(elderlyResponse.data.count || elderlyResponse.data.length || 0);
                setNursesCount(nursesResponse.data.length || 0);
                setDoctorsCount(doctorsResponse.data.length || 0);
            } catch (error) {
                console.error('Errore caricamento dati:', error);
                // Fallback con dati di esempio se il DB non è disponibile
                setElderlyCount(25);
                setNursesCount(8);
                setDoctorsCount(5);
            } finally {
                setElderlyLoading(false);
            }
        };
        
        loadData();
    }, [axiosPrivate, auth?.accessToken]);

    // Memorizza la funzione di gestione per evitare re-render
    const handleNewAlert = useCallback((newAlert) => {
        setAlerts(prevAlerts => {
            if (prevAlerts.some(a => a.id === newAlert.id)) {
                return prevAlerts;
            }
            return [newAlert, ...prevAlerts];
        });
    }, [t]);

    // Socket listener ottimizzato
    useEffect(() => {
        const setupSocketListeners = () => {
            if (socket && typeof socket.on === 'function') {
                socket.on('newAlert', handleNewAlert);
            } else {
                console.error(t('dashboard.errors.socketInit', "Socket.IO non inizializzato correttamente in DashboardPage"));
            }
        };
        
        const timer = setTimeout(setupSocketListeners, 100);
        
        return () => {
            clearTimeout(timer);
            if (socket && typeof socket.off === 'function') {
                socket.off('newAlert', handleNewAlert);
            }
        };
    }, [handleNewAlert, t]);

    // Meteo fetch con lazy loading
    useEffect(() => {
        const fetchWeatherData = async () => {
            try {
                const response = await axiosPrivate.get('/api/weather/current?city=Florence');
                setTemp(response.data.temp);
                setWeather(response.data.description);
                setHumidity(response.data.humidity);
                setWeatherIcon(`/api/weather/icon/${response.data.icon}`);
            } catch (error) {
                console.error(t('dashboard.errors.weatherFetch', 'Errore nel recupero dati meteo:'), error);
            }
        };

        fetchWeatherData();
    }, [t, axiosPrivate]);

    // Sensore T&H fetch - usa /sensors per ottenere anche associazioni a stanza/corridoio
    useEffect(() => {
        const fetchSensorData = async () => {
            try {
                // Recupera lista sensori con informazioni di localizzazione
                const response = await axiosPrivate.get('/sensors');
                if (response.data && response.data.length > 0) {
                    // Prendi il primo sensore (il più recente grazie all'ordinamento del backend)
                    const first = response.data[0];
                    setSensorTh({
                        sensor_id: first.sensor_id,
                        temperature: first.temperature,
                        humidity: first.humidity,
                        battery_level: first.battery_level,
                        timestamp: first.last_seen || first.timestamp,
                        room_id: first.room_id,
                        room_number: first.room_number,
                        corridor_floor_id: first.corridor_floor_id,
                        corridor_wing_id: first.corridor_wing_id,
                        corridor_floor_name: first.corridor_floor_name,
                        corridor_wing_name: first.corridor_wing_name
                    });
                }
            } catch (error) {
                console.error('Errore nel recupero dati sensore:', error);
            }
        };

        if (auth?.accessToken) {
            fetchSensorData();
        }

        // Socket.IO listener per aggiornamenti real-time
        const handleThSensorUpdate = (data) => {
            if (data) {
                setSensorTh(prev => ({
                    ...prev,
                    sensor_id: data.sensor_id,
                    temperature: data.temperature,
                    humidity: data.humidity,
                    battery_level: data.battery_level,
                    timestamp: data.timestamp
                }));
            }
        };

        if (socket && typeof socket.on === 'function') {
            socket.on('th_sensor_update', handleThSensorUpdate);
        }

        return () => {
            if (socket && typeof socket.off === 'function') {
                socket.off('th_sensor_update', handleThSensorUpdate);
            }
        };
    }, [auth?.accessToken, axiosPrivate]);

    // Dati delle card memoizzati
    const cardItems = useMemo(() => [
        {
            value: elderlyLoading ? '...' : (elderlyCount || 0),
            title: t('dashboard.cards.elderly', "Numero Anziani"),
            icon: <Person fontSize="inherit" />
        },
        {
            value: elderlyLoading ? '...' : (nursesCount || 0),
            title: t('dashboard.cards.nurses', "Personale Infermieristico"),
            icon: <People fontSize="inherit" />
        },
        {
            value: elderlyLoading ? '...' : (doctorsCount || 0),
            title: t('dashboard.cards.doctors', "Personale Medico"),
            icon: <People fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (appointmentsCount || 0),
            title: t('dashboard.cards.appointments', "Appuntamenti"),
            icon: <EditCalendar fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (eventsCount || 0),
            title: t('dashboard.cards.events', "Eventi"),
            icon: <EditCalendar fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (maintenanceCount || 0),
            title: t('dashboard.cards.maintenance', "Manutenzioni"),
            icon: <EditCalendar fontSize="inherit" />
        }
    ], [elderlyCount, nursesCount, doctorsCount, appointmentsCount, eventsCount, maintenanceCount, elderlyLoading, appointmentsLoading, t]);

    // Opzioni chart memoizzate
    const chartOptions = useMemo(() => ({
        plugins: {
            legend: { display: false }
        },
        maintainAspectRatio: true,
        responsive: true
    }), []);

    // Dati chart memoizzati
    const chartData = useMemo(() => ({
        labels: [
            t('dashboard.charts.labels.homeCare', "Assistenza Domiciliare"), 
            t('dashboard.charts.labels.memoryCare', "Assistenza Memoria"), 
            t('dashboard.charts.labels.specialiseCare', "Assistenza Specialistica"), 
            t('dashboard.charts.labels.nursingCare', "Assistenza Infermieristica")
        ],
        datasets: [
            {
                label: t('dashboard.charts.population', "Popolazione"),
                data: [65, 55, 130, 140, 150],
                // Colori verranno applicati dinamicamente dal tema tramite Chart.js
            }
        ]
    }), [t]);

    // Loading state semplificato - rimuovo il check del token dal loading generale
    const isLoading = (elderlyLoading || alertsLoading || sensorsLoading) && auth?.accessToken;

    // Calcola il saluto in base all'ora
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return t('dashboard.greeting.morning', 'Buongiorno');
        if (hour < 18) return t('dashboard.greeting.afternoon', 'Buon pomeriggio');
        return t('dashboard.greeting.evening', 'Buonasera');
    };

    // Se non abbiamo token, mostriamo dashboard basic
    if (!auth?.accessToken) {
        return (
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: "calc(100% - 10px)"
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            {t('dashboard.title', "Dashboard")}
                        </Typography>
                        <Typography variant="body1">
                            {t('dashboard.loadingAuth', "Verifica autenticazione in corso...")}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: "calc(100% - 10px)",
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: 3
                }}
            >
                <Box sx={{ width: '60%', maxWidth: 400 }}>
                    <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
                </Box>
                <Typography variant="h6" color="text.secondary">
                    {t('dashboard.loading', "Caricamento dashboard...")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                p: 3,
                width: "calc(100% - 10px)",
                minHeight: '100vh',
                bgcolor: 'background.default'
            }}
        >
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Grid container spacing={3}>
                    {/* Header Dashboard con saluto */}
                    <Grid item xs={12} md={4}>
                        <motion.div variants={itemVariants}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>
                                    {getGreeting()}, {auth?.user?.name || t('dashboard.greeting.operator', 'Operatore')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {t('dashboard.summary', 'Ecco un riepilogo delle attività della struttura')}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Widget Sensore T&H */}
                    <Grid item xs={12} md={4}>
                        <SensorThWidget 
                            t={t}
                            sensor={sensorTh}
                            navigate={navigate}
                        />
                    </Grid>

                    {/* Widget Meteo */}
                    <Grid item xs={12} md={4}>
                        <WeatherWidget 
                            t={t}
                            temp={temp}
                            weather={weather}
                            humidity={humidity}
                            weatherIcon={weatherIcon}
                        />
                    </Grid>

                    {/* Widget Alert con lazy loading */}
                    <Grid item xs={12}>
                        <motion.div variants={itemVariants}>
                            <Card sx={{ p: 2, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Avatar sx={{ bgcolor: 'error.main', width: 36, height: 36 }}>
                                        <Notifications sx={{ fontSize: 20 }} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="bold">
                                        {t('dashboard.widgets.activeAlerts', "Alert Attivi")}
                                    </Typography>
                                    {alerts.length > 0 && (
                                        <Chip 
                                            label={alerts.length} 
                                            size="small" 
                                            color="error"
                                            sx={{ fontWeight: 700, ml: 1 }}
                                        />
                                    )}
                                </Box>
                                <Suspense fallback={
                                    <Box sx={{ p: 2 }}>
                                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                                    </Box>
                                }>
                                    <LazyAlertsWidget alerts={alerts} setAlerts={setAlerts} />
                                </Suspense>
                            </Card>
                        </motion.div>
                    </Grid>

                    {/* Cards contatori con animazioni staggered */}
                    {cardItems.map((item, index) => (
                        <DashboardCard key={index} item={item} index={index} t={t} />
                    ))}

                    {/* Widget 3 colonne: Appuntamenti, Eventi, Manutenzioni */}
                    <AppointmentsWidget 
                        appointments={appointments}
                        navigate={navigate}
                        t={t}
                    />

                    <EventsWidget
                        events={events}
                        navigate={navigate}
                        t={t}
                    />

                    <Grid item xs={12} md={4}>
                        <motion.div variants={itemVariants} style={{ height: '100%' }}>
                            <MaintenanceWidget />
                        </motion.div>
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};

export default DashboardPage;